import { useEffect, useMemo, useState } from 'react';
import { sortHand, type BluffLegal, type BluffView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { SeatView, MySeat, useNarrow, useSpeech } from './common';

const RANK_LABEL = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const MAX_PLAY = 4;

export function BluffTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as BluffView;
  const legal = useGame((s) => s.legal) as BluffLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [picked, setPicked] = useState<string[]>([]);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const ended = view.phase === 'MATCH_END';
  const claim = view.lastClaim;
  const reveal = view.lastReveal;
  const rank = RANK_LABEL[view.required]!;

  useEffect(() => {
    setPicked([]);
  }, [view.turn, view.pileCount, view.phase]);

  const toggle = (c: string) => {
    if (!myTurn || !legal?.canPlay || acting) return;
    setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : p.length >= MAX_PLAY ? p : [...p, c]));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{t('bh.cards', { n: view.handCounts[s] ?? 0 })}</span>}
            tags={
              <>
                {claim && claim.seat === s && <span className="tag seen">{`${claim.count}× ${RANK_LABEL[claim.rank]}`}</span>}
                {ended && view.winner === s && <span className="tag blind">{t('bl.winner')}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered" style={{ gap: 8 }}>
          <div className="center-note">
            {t('bl.claim')}: <strong style={{ fontSize: '1.4em', color: '#fff' }}>{rank}</strong>
          </div>
          <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('bl.pile', { n: view.pileCount })}>
            <div style={{ position: 'relative', width: 90, height: narrow ? 72 : 90 }}>
              {Array.from({ length: Math.min(view.pileCount, 6) }).map((_, i) => (
                <div key={i} style={{ position: 'absolute', left: 10 + i * 6, top: i * 2 }}>
                  <PlayingCard faceDown size="sm" />
                </div>
              ))}
              {view.pileCount === 0 && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('bl.empty')}</span>}
            </div>
            <span className="center-note">{t('bl.pile', { n: view.pileCount })}</span>
          </div>
          {claim && !ended && (
            <span className="pill accent" role="status">
              {t('bl.claimed', { name: name(claim.seat), n: claim.count, rank: RANK_LABEL[claim.rank]! })}
            </span>
          )}
          {reveal && !claim && (
            <div className="card glass" style={{ textAlign: 'center', padding: 8 }} role="status">
              <div style={{ fontSize: 'var(--fs-xs)' }}>
                <strong>{reveal.truthful ? t('bl.honest', { name: name(reveal.loser), n: reveal.pileSize }) : t('bl.caught', { name: name(reveal.loser), n: reveal.pileSize })}</strong>
              </div>
              <div className="row" style={{ justifyContent: 'center', marginTop: 4 }}>
                {reveal.cards.map((c) => (
                  <PlayingCard key={c} id={c} size="sm" />
                ))}
              </div>
            </div>
          )}
          {ended && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{view.winner === null ? t('sv.over') : t('bl.wins', { name: name(view.winner) })}</strong>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('bh.cards', { n: view.handCounts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal && (
          <div className="action-bar" role="group" aria-label={t('sc.choose')} style={{ flexWrap: 'wrap', gap: 8 }}>
            {legal.canCall && claim && (
              <button className="btn danger" style={{ flex: '1 1 40%', whiteSpace: 'normal' }} disabled={acting} onClick={() => void act({ type: 'call' })}>
                {t('bl.call', { name: name(claim.seat) })}
              </button>
            )}
            {legal.canAccept && claim && (
              <button className="btn primary" style={{ flex: '1 1 40%', whiteSpace: 'normal' }} disabled={acting} onClick={() => void act({ type: 'accept' })}>
                {t('bl.accept', { name: name(claim.seat) })}
              </button>
            )}
            {legal.canPlay && (
              <button className="btn primary" style={{ flex: '1 1 40%', whiteSpace: 'normal' }} disabled={picked.length === 0 || acting} onClick={() => void act({ type: 'play', cards: picked })}>
                {picked.length === 0 ? t('bl.pick', { rank }) : t('bl.play', { n: picked.length, rank })}
              </button>
            )}
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.62}>
          {hand.map((c) => (
            <PlayingCard key={c} id={c} size={narrow ? 'md' : 'lg'} selected={picked.includes(c)} playable={Boolean(myTurn && legal?.canPlay)} onClick={() => toggle(c)} />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('bh.player')}</th>
              <th>{t('bh.cardsLeft')}</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3]
              .sort((a, b) => (view.winner === a ? -1 : view.winner === b ? 1 : (view.handCounts[a] ?? 0) - (view.handCounts[b] ?? 0)))
              .map((s, i) => (
                <tr key={s}>
                  <td>{i + 1}</td>
                  <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                  <td className="num">{view.handCounts[s] ?? 0}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('bl.scoreHint')}</p>
      </Modal>
    </>
  );
}
